import ArrowForwardOutlined from '@mui/icons-material/ArrowForwardOutlined';
import AutoAwesomeOutlined from '@mui/icons-material/AutoAwesomeOutlined';
import ExploreOutlined from '@mui/icons-material/ExploreOutlined';
import UndoOutlined from '@mui/icons-material/UndoOutlined';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import { keyframes } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { Fragment, type ReactNode, useState } from 'react';
import caret from '../../../../assets/alison-run/caret.svg';
import { type FieldValues, missingRequired, withFieldValue } from '../../fieldValues.js';
import { IconBadge } from '../../IconBadge.js';
import { Pill, PRIMARY_PILL_SX } from '../../Pill.js';
import { buildRunParams, openingValues } from '../../runParams.js';
import { SelectableCard } from '../../SelectableCard.js';
import { useFieldOptions } from '../../useFieldOptions.js';
import { WorkflowFieldControl } from '../../WorkflowFields.js';
import { iconFor } from '../../workflowIcons.js';
import type { AlisonRunCopy, WorkflowStep } from '../../workflows.types.js';
import { useAlisonRun } from '../AlisonRunProvider.js';
import type { AlisonSetup } from '../data/alisonLive.js';
import {
  type ChainCard,
  fillTemplate,
  onlyOption,
  pathOptions,
  recommendedOption,
} from '../data/alisonRun.js';
import { DrawerFooter, DrawerHeader, GradientButton } from './AlisonDrawerChrome.js';
import { DestinationArtwork } from './DestinationArtwork.js';

const stageIn = keyframes`
  from { opacity: 0.4; }
  to { opacity: 1; }
`;

// Who put a step on the path: the person, Alison choosing for them, or Alison adding the
// only step that could follow.
type PickedBy = 'you' | 'alison' | 'added';
type RouteParams = Record<string, unknown>;

interface Picks {
  steps: string[];
  by: Record<string, PickedBy>;
}

function StepChip({
  card,
  position,
  onChange,
}: {
  card: ChainCard;
  position: number;
  onChange?: () => void;
}): ReactNode {
  const { copy } = useAlisonRun();
  const Icon = iconFor(card.icon, AutoAwesomeOutlined);
  return (
    <Chip
      variant={onChange ? 'outlined' : 'soft'}
      icon={<Icon />}
      label={`${position}. ${card.name}`}
      {...(onChange
        ? {
            onDelete: onChange,
            deleteIcon: (
              <Tooltip title={copy.changeStep}>
                <UndoOutlined
                  aria-label={fillTemplate(copy.changeStepLabel, { title: card.name })}
                />
              </Tooltip>
            ),
          }
        : {})}
      sx={{ bgcolor: onChange ? 'background.paper' : undefined }}
    />
  );
}

function pickedLabel(copy: AlisonRunCopy, by: PickedBy | undefined): string {
  if (by === 'alison') return copy.pickedByAlison;
  if (by === 'added') return copy.addedForYou;
  return copy.pickedByYou;
}

// Appends `path`, then every step that is the only one able to follow.
function extended(
  cards: ChainCard[],
  root: string,
  picks: Picks,
  path: string[],
  by: PickedBy,
  params: RouteParams,
) {
  const steps = [...picks.steps, ...path];
  const marks = { ...picks.by, ...Object.fromEntries(path.map((step) => [step, by])) };
  for (let only = onlyOption(cards, [root, ...steps], params); only !== null; ) {
    steps.push(...only);
    for (const step of only) marks[step] = 'added';
    only = onlyOption(cards, [root, ...steps], params);
  }
  return { steps, by: marks };
}

// Alison's route from where the path is: the recommended step each time, or the only one.
function chosenByAlison(
  cards: ChainCard[],
  root: string,
  picks: Picks,
  params: RouteParams,
): Picks {
  const steps = [...picks.steps];
  const marks = { ...picks.by };
  const nextOf = () =>
    recommendedOption(cards, [root, ...steps], params) ??
    onlyOption(cards, [root, ...steps], params);
  for (let next = nextOf(); next !== null; next = nextOf()) {
    const by = pathOptions(cards, [root, ...steps], params).length === 1 ? 'added' : 'alison';
    steps.push(...next);
    for (const step of next) marks[step] = by;
  }
  return { steps, by: marks };
}

// Builds a path from the page's action one step at a time, then runs it.
export function AlisonPathBuilder({ root }: { root: string }): ReactNode {
  const { copy, cards, displays, start, setup, details, formReady, missing, blockers } =
    useAlisonRun();
  // Only what the user changed, so the rest keeps following the page and earlier steps.
  const [edits, setEdits] = useState<Record<string, FieldValues>>({});
  const [picks, setPicks] = useState<Picks>(() =>
    extended(cards, root, { steps: [], by: {} }, [], 'added', setup?.params ?? {}),
  );
  const [selection, setSelected] = useState<string[] | null>(null);
  const [preview, setPreview] = useState(false);
  const [setupOpen, setSetupOpen] = useState(false);
  const { steps } = picks;
  const cardOf = (actionType: string): ChainCard | undefined =>
    cards.find((card) => card.workflowId === actionType);
  const rootCard = cardOf(root);
  const route = [root, ...steps];
  const currentCard = cardOf(route.at(-1) ?? root);
  if (rootCard === undefined || currentCard === undefined) return null;

  const visibleSteps = steps
    .map((actionType, index) => ({ card: cardOf(actionType), index }))
    .flatMap(({ card, index }) => (card === undefined || card.auxiliary ? [] : [{ card, index }]));
  const visibleCount = visibleSteps.length + 1;
  const outcome = currentCard.resultTitle.toLowerCase();
  const ready = route.every((actionType) => displays[actionType] !== undefined);
  const alisonChose = Object.values(picks.by).includes('alison');
  const changeTo = (next: string[]): void => {
    setPicks((current) => ({
      steps: next,
      by: Object.fromEntries(next.map((step) => [step, current.by[step] ?? 'you'])),
    }));
    setSelected(null);
    setPreview(false);
  };
  // Back to before the visible step at `index`, with its auxiliary steps.
  const changeFrom = (index: number): void =>
    changeTo(steps.slice(0, (visibleSteps[index - 1]?.index ?? -1) + 1));
  const proceed = (): void => {
    if (selected === null) return;
    setPicks(extended(cards, root, picks, selected, 'you', inherited));
    setSelected(null);
  };
  const letAlisonChoose = (): void => {
    setPicks(chosenByAlison(cards, root, picks, inherited));
    setSelected(null);
    setPreview(true);
  };
  let inherited = setup?.params ?? {};
  const configurations = steps.flatMap((actionType) => {
    const step = details
      .find((detail) => detail.workflowId === actionType)
      ?.steps.find((step) => step.kind === 'config');
    if (step === undefined || setup === undefined) return [];
    const opening = openingValues(step.fields, null, inherited, setup.leadingKpi);
    const values = { ...opening, ...edits[actionType] };
    const params = buildRunParams(step.fields, values);
    const openingParams = buildRunParams(step.fields, opening);
    const changed = Object.fromEntries(
      Object.entries(params).filter(
        ([key, value]) => JSON.stringify(value) !== JSON.stringify(openingParams[key]),
      ),
    );
    inherited = { ...inherited, ...params };
    return [{ actionType, step, opening, values, changed }];
  });
  const options = pathOptions(cards, route, inherited);
  const recommended = recommendedOption(cards, route, inherited)?.at(-1) ?? null;
  // A pick the route's params have since ruled out is no pick.
  const selected = options.some((path) => path.join() === selection?.join()) ? selection : null;
  const ended = options.length === 0;
  const showPreview = ended || preview;
  const configurable = configurations.filter(({ step }) => step.fields.length > 0);
  const missingFields = [
    ...missing,
    ...configurations.flatMap(({ step, values }) => missingRequired(step.fields, values)),
  ];
  const setupShown = setupOpen || missingFields.length > 0 || Object.keys(edits).length > 0;
  const blocked = !formReady || missingFields.length > 0 || blockers.length > 0;
  const missingHint = [
    ...(missingFields.length === 0 || copy.missingFields === undefined
      ? []
      : [fillTemplate(copy.missingFields, { fields: missingFields.join(', ') })]),
    ...blockers,
  ].join(' ');
  const paramsByAction = Object.fromEntries(
    configurations.flatMap(({ actionType, changed }) =>
      Object.keys(changed).length === 0 ? [] : [[actionType, changed]],
    ),
  );
  const runLabel =
    steps.length === 0
      ? fillTemplate(copy.runStart, { title: rootCard.name })
      : fillTemplate(copy.runTo, { outcome });

  return (
    <Stack sx={{ height: '100%' }}>
      <DrawerHeader subtitle={copy.choosingSubtitle} height={88} running={false} />

      <Stack
        component="section"
        aria-label={copy.pathLabel}
        sx={{ flex: 1, minHeight: 0, overflowY: 'auto', gap: 4, p: 5 }}
      >
        {showPreview ? (
          <PathReady
            rootCard={rootCard}
            destination={currentCard}
            visibleSteps={visibleSteps.map(({ card }) => card)}
            pickedBy={picks.by}
            alisonChose={alisonChose}
            outcome={outcome}
            onChange={changeFrom}
            setup={
              setup === undefined || configurable.length === 0 ? null : (
                <Stack sx={{ gap: 3 }}>
                  <Stack direction="row" sx={{ alignItems: 'center', gap: 1 }}>
                    <Typography variant="h3">{copy.startingSetup}</Typography>
                    <Typography aria-hidden sx={{ color: 'text.disabled' }}>
                      ·
                    </Typography>
                    <Button
                      size="small"
                      aria-expanded={setupShown}
                      onClick={() => setSetupOpen((open) => !open)}
                    >
                      {copy.change}
                    </Button>
                  </Stack>
                  {setupShown &&
                    configurable.map(({ actionType, step, opening, values }) => (
                      <StepConfiguration
                        key={actionType}
                        step={step}
                        values={values}
                        setup={setup}
                        onChange={(next) =>
                          setEdits((current) => ({
                            ...current,
                            [actionType]: Object.fromEntries(
                              Object.entries(next).filter(
                                ([key, value]) =>
                                  JSON.stringify(value) !== JSON.stringify(opening[key]),
                              ),
                            ),
                          }))
                        }
                      />
                    ))}
                </Stack>
              )
            }
          />
        ) : (
          <>
            <Stack
              direction="row"
              sx={{ gap: 1, alignItems: 'center', flexWrap: 'wrap', rowGap: 2 }}
            >
              <StepChip card={rootCard} position={1} />
              {visibleSteps.map(({ card }, index) => (
                <Fragment key={card.workflowId}>
                  <ArrowForwardOutlined sx={{ fontSize: 14, color: 'text.disabled' }} />
                  <StepChip card={card} position={index + 2} onChange={() => changeFrom(index)} />
                </Fragment>
              ))}
            </Stack>
            <Stack
              component="aside"
              aria-live="polite"
              direction="row"
              sx={{
                gap: 3,
                alignItems: 'center',
                p: 3,
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'border.light',
                bgcolor: 'primary.bg',
              }}
            >
              <ExploreOutlined sx={{ fontSize: 20, color: 'primary.main', flexShrink: 0 }} />
              <Box>
                <Typography variant="overline" component="p" sx={{ color: 'primary.main' }}>
                  {copy.leadsLabel}
                </Typography>
                <Typography>
                  {steps.length === 0
                    ? fillTemplate(copy.leadsStart, { title: rootCard.name })
                    : fillTemplate(copy.leadsStep, { outcome })}
                </Typography>
              </Box>
            </Stack>
            <Stack
              key={route.join('/')}
              sx={{
                gap: 1,
                animation: `${stageIn} 0.2s ease-out`,
                '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
              }}
            >
              <Stack direction="row" sx={{ alignItems: 'center', gap: 2 }}>
                <Pill sx={PRIMARY_PILL_SX}>
                  {fillTemplate(copy.stepNumber, { current: visibleCount + 1 })}
                </Pill>
                <Typography variant="overline" sx={{ color: 'text.secondary' }}>
                  {options.length === 1
                    ? fillTemplate(copy.followsOne, { title: currentCard.name })
                    : fillTemplate(copy.follows, {
                        count: options.length,
                        title: currentCard.name,
                      })}
                </Typography>
              </Stack>
              <Typography variant="h2" component="h3" sx={{ mt: 2 }}>
                {fillTemplate(copy.nextPrompt, { title: currentCard.name })}
              </Typography>
              <Typography sx={{ color: 'text.secondary' }}>{copy.nextHint}</Typography>
              <Box
                role="radiogroup"
                aria-label={fillTemplate(copy.nextPrompt, { title: currentCard.name })}
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: `repeat(${options.length === 3 ? 3 : 2}, 1fr)`,
                  },
                  gap: 3,
                  mt: 3,
                }}
              >
                {options.map((path) => {
                  const option = cardOf(path.at(-1) ?? '');
                  if (option === undefined) return null;
                  const following = pathOptions(cards, [...route, ...path], inherited);
                  const checked = selected?.at(-1) === option.workflowId;
                  return (
                    <SelectableCard
                      key={option.workflowId}
                      testId={`path-step-${option.workflowId}`}
                      onClick={() => setSelected(path)}
                      ariaPressed={checked}
                      {...(checked ? { accent: 'primary.main' } : {})}
                      icon={
                        <IconBadge
                          icon={iconFor(option.icon, AutoAwesomeOutlined)}
                          size={40}
                          iconSize={22}
                        />
                      }
                      title={option.name}
                      badge={
                        option.workflowId === recommended ? (
                          <Pill sx={PRIMARY_PILL_SX}>{copy.recommendedLabel}</Pill>
                        ) : undefined
                      }
                      description={option.description}
                      detail={following.length === 0 ? null : copy.thenLabel}
                      chips={following.flatMap((next) => {
                        const name = cardOf(next.at(-1) ?? '')?.name;
                        return name === undefined ? [] : [name];
                      })}
                      footer={following.length === 0 ? copy.endsPath : null}
                    />
                  );
                })}
              </Box>
            </Stack>
          </>
        )}
        {showPreview && missingHint !== '' && (
          <Typography role="alert" color="error">
            {missingHint}
          </Typography>
        )}
      </Stack>

      <DrawerFooter
        note={
          showPreview
            ? copy.introFootnote
            : steps.length === 0
              ? copy.pathNote
              : fillTemplate(copy.pathSummary, { count: visibleCount, outcome })
        }
      >
        {showPreview ? (
          <>
            {(!ended || visibleSteps.length > 0) && (
              <Button
                onClick={() => (ended ? changeFrom(visibleSteps.length - 1) : setPreview(false))}
              >
                {copy.editPath}
              </Button>
            )}
            <GradientButton
              busy={!ready}
              disabled={blocked}
              onClick={() => start(route.length - 1, route, paramsByAction)}
            >
              {runLabel}
              {ready ? (
                <Box component="img" src={caret} alt="" sx={{ width: 16, height: 16 }} />
              ) : (
                <CircularProgress size={16} color="inherit" aria-hidden />
              )}
            </GradientButton>
          </>
        ) : (
          <>
            {recommended !== null && (
              <Button onClick={letAlisonChoose}>{copy.letAlisonChoose}</Button>
            )}
            <Button variant="outlined" onClick={() => setPreview(true)}>
              {runLabel}
            </Button>
            <GradientButton disabled={selected === null} onClick={proceed}>
              {copy.continueLabel}
            </GradientButton>
          </>
        )}
      </DrawerFooter>
    </Stack>
  );
}

function PathReady({
  rootCard,
  destination,
  visibleSteps,
  pickedBy,
  alisonChose,
  outcome,
  onChange,
  setup,
}: {
  rootCard: ChainCard;
  destination: ChainCard;
  visibleSteps: ChainCard[];
  pickedBy: Record<string, PickedBy>;
  alisonChose: boolean;
  outcome: string;
  onChange: (index: number) => void;
  setup: ReactNode;
}): ReactNode {
  const { copy } = useAlisonRun();
  const route = [rootCard, ...visibleSteps];
  return (
    <Stack aria-label={copy.pathReady} sx={{ gap: 4 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        sx={{ gap: 4, alignItems: { xs: 'flex-start', sm: 'center' } }}
      >
        <Stack
          aria-hidden="true"
          direction="row"
          sx={{
            width: 140,
            minHeight: 110,
            flexShrink: 0,
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1,
            p: 2,
            bgcolor: 'primary.bg',
            borderRadius: 2,
            color: 'primary.main',
          }}
        >
          <DestinationArtwork card={destination} />
        </Stack>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h2" component="h3">
            {copy.pathReady}
          </Typography>
          <Typography sx={{ mt: 2, color: 'text.secondary' }}>
            {route.length === 1 && copy.pathReadyBodyOne !== undefined
              ? fillTemplate(copy.pathReadyBodyOne, { outcome })
              : fillTemplate(copy.pathReadyBody, { count: route.length, outcome })}
          </Typography>
        </Box>
      </Stack>

      {alisonChose && (
        <Stack
          component="aside"
          direction="row"
          sx={{
            gap: 2,
            alignItems: 'center',
            p: 3,
            borderRadius: 1,
            bgcolor: 'primary.bg',
            color: 'primary.main',
          }}
        >
          <AutoAwesomeOutlined sx={{ fontSize: 18 }} />
          <Typography sx={{ color: 'text.primary' }}>{copy.alisonChosePath}</Typography>
        </Stack>
      )}

      <Stack component="ol" aria-label={copy.pathLabel} sx={{ gap: 2, m: 0, p: 0 }}>
        {route.map((card, index) => {
          const by = index === 0 ? undefined : pickedBy[card.workflowId];
          return (
            <Stack
              component="li"
              key={card.workflowId}
              direction="row"
              sx={{
                gap: 3,
                alignItems: 'center',
                p: 3,
                listStyle: 'none',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
                bgcolor: 'background.paper',
              }}
            >
              <IconBadge icon={iconFor(card.icon, AutoAwesomeOutlined)} size={32} iconSize={18} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600 }}>{`${index + 1}. ${card.name}`}</Typography>
                {index > 0 && (
                  <Typography
                    sx={{ fontSize: 12, color: by === 'you' ? 'text.muted' : 'primary.main' }}
                  >
                    {pickedLabel(copy, by)}
                    {by === 'added' &&
                      ` · ${fillTemplate(copy.addedForYouReason, { title: card.name })}`}
                  </Typography>
                )}
              </Box>
              {index > 0 && (
                <Button
                  size="small"
                  aria-label={fillTemplate(copy.changeStepLabel, { title: card.name })}
                  onClick={() => onChange(index - 1)}
                >
                  {copy.change}
                </Button>
              )}
            </Stack>
          );
        })}
      </Stack>

      <Stack sx={{ gap: 2 }}>
        <Typography variant="overline" sx={{ color: 'text.secondary' }}>
          {copy.outputsLabel}
        </Typography>
        <Stack direction="row" sx={{ gap: 1, flexWrap: 'wrap' }}>
          {route.map((card) => (
            <Pill key={card.workflowId} sx={PRIMARY_PILL_SX}>
              {card.resultTitle}
            </Pill>
          ))}
        </Stack>
      </Stack>

      {setup}
    </Stack>
  );
}

export function StepConfiguration({
  step,
  values,
  setup,
  onChange,
}: {
  step: WorkflowStep;
  values: FieldValues;
  setup: AlisonSetup;
  onChange: (values: FieldValues) => void;
}): ReactNode {
  const optionsFor = useFieldOptions(
    step,
    values,
    setup.scope.dateRange,
    setup.scope.accountIds ?? [],
    setup.leadingKpi,
  );
  return (
    <Stack sx={{ gap: 2 }}>
      <Typography variant="h3">{step.title}</Typography>
      {step.fields.map((field) => (
        <WorkflowFieldControl
          key={field.key}
          field={field}
          values={values}
          optionsFor={optionsFor}
          onChange={(key, value) => onChange(withFieldValue(step.fields, values, key, value))}
        />
      ))}
    </Stack>
  );
}
